/*
 * Magnifying and moving a picture: the arithmetic, with no DOM (as
 * utils/microGeometry.js is for the slide canvas). hooks/usePanZoom.js turns
 * pointers, the wheel and keys into these calls.
 *
 * A view is { scale, x, y }: the content is scaled about its centre, then
 * moved by (x, y) screen pixels. Every point here is relative to the frame's
 * centre. `frame` and `content` are { width, height } in screen pixels, the
 * content's as laid out at scale 1 (a picture fitted inside the frame is
 * usually smaller than it on one side).
 */
export const MIN_SCALE = 1;
export const MAX_SCALE = 8;
/** One step of the zoom buttons, the keys and a mouse wheel's notch. */
export const STEP = 1.5;

export const IDENTITY = Object.freeze({ scale: 1, x: 0, y: 0 });

export const clampScale = (scale, min = MIN_SCALE, max = MAX_SCALE) => Math.min(max, Math.max(min, scale));

/** How far the content may move each way at this scale: never past its edge,
 *  so it can never be dragged out of view, and not at all on a side where it
 *  fits inside the frame. */
export function panLimits(scale, frame, content = frame) {
  return {
    x: Math.max(0, ((content?.width || 0) * scale - (frame?.width || 0)) / 2),
    y: Math.max(0, ((content?.height || 0) * scale - (frame?.height || 0)) / 2),
  };
}

export function clampView(view, frame, content = frame, min = MIN_SCALE, max = MAX_SCALE) {
  const scale = clampScale(view.scale, min, max);
  if (scale <= min && min <= 1) return { scale, x: 0, y: 0 };
  const limit = panLimits(scale, frame, content);
  return {
    scale,
    x: Math.min(limit.x, Math.max(-limit.x, view.x)),
    y: Math.min(limit.y, Math.max(-limit.y, view.y)),
  };
}

/** Zoom to `scale`, keeping the focal point (relative to the frame's centre)
 *  where it is on the screen. */
export function zoomAround(view, scale, focal = { fx: 0, fy: 0 }, frame, content = frame, min, max) {
  const next = clampScale(scale, min, max);
  const ratio = next / view.scale;
  const fx = focal.fx || 0;
  const fy = focal.fy || 0;
  return clampView({ scale: next, x: fx - (fx - view.x) * ratio, y: fy - (fy - view.y) * ratio },
    frame, content, min, max);
}

const distance = (a, b) => Math.hypot(a.fx - b.fx, a.fy - b.fy) || 1;
const middle = (a, b) => ({ fx: (a.fx + b.fx) / 2, fy: (a.fy + b.fy) / 2 });

/**
 * Two fingers: how far apart they are now against when they touched decides
 * the scale, and where their midpoint has moved decides the move -- both at
 * once, so pinching while dragging does both. `start` is { view, a, b }, the
 * view and the two points when the second finger came down.
 */
export function pinchView(start, a, b, frame, content = frame, min, max) {
  const scale = clampScale(start.view.scale * (distance(a, b) / distance(start.a, start.b)), min, max);
  const ratio = scale / start.view.scale;
  const was = middle(start.a, start.b);
  const now = middle(a, b);
  return clampView({
    scale,
    x: now.fx - (was.fx - start.view.x) * ratio,
    y: now.fy - (was.fy - start.view.y) * ratio,
  }, frame, content, min, max);
}

/** A drag of one finger (or the mouse) by (dx, dy) screen pixels. */
export const panView = (view, dx, dy, frame, content = frame, min, max) =>
  clampView({ ...view, x: view.x + dx, y: view.y + dy }, frame, content, min, max);

const LINE_PX = 16;
const PAGE_PX = 400;
/** A mouse wheel's notch is about 100 px; a trackpad scrolls a few at a time. */
const NOTCH_PX = 50;

/**
 * The factor one wheel event zooms by: a whole STEP for a mouse wheel's notch
 * (so a notch is a button press), and in proportion to the distance for a
 * trackpad's stream of small deltas, which a fixed step made jumpy.
 */
export function wheelFactor(event) {
  const unit = event.deltaMode === 1 ? LINE_PX : event.deltaMode === 2 ? PAGE_PX : 1;
  const delta = (event.deltaY || 0) * unit;
  if (!delta) return 1;
  if (Math.abs(delta) >= NOTCH_PX) return delta < 0 ? STEP : 1 / STEP;
  return Math.min(STEP, Math.max(1 / STEP, Math.exp(-delta * 0.01)));
}

/** A screen point, relative to the frame's centre. */
export const focalOf = (clientX, clientY, rect) => ({
  fx: clientX - (rect?.left || 0) - (rect?.width || 0) / 2,
  fy: clientY - (rect?.top || 0) - (rect?.height || 0) / 2,
});

/** Where a screen point falls in the content as laid out at scale 1, relative
 *  to its centre: the inverse of the view's transform. */
export function clientToContent(clientX, clientY, rect, view) {
  const { fx, fy } = focalOf(clientX, clientY, rect);
  return { x: (fx - view.x) / view.scale, y: (fy - view.y) / view.scale };
}

/** The CSS transform of a view. */
export const transformOf = view => `translate(${view.x}px, ${view.y}px) scale(${view.scale})`;
